import { useEffect, useState } from "react";
import { Star, MessageSquare, Flag, Loader2, ShieldCheck, Car } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/ui-helpers";
import { Stars, RatingDistribution } from "@/components/reviews/Stars";
import { toast } from "sonner";

export default function SellerReviews() {
  const [summary, setSummary] = useState(null);
  const [reviews, setReviews] = useState(null);
  const [filter, setFilter] = useState("all");
  const [rideReviews, setRideReviews] = useState([]);

  const loadSummary = () => api.get("/reviews/seller/summary").then((r) => setSummary(r.data)).catch(() => setSummary(null));
  const loadRides = () => api.get("/seller/driver-reviews").then((r) => setRideReviews(r.data.reviews || [])).catch(() => setRideReviews([]));
  const loadReviews = () => {
    const params = filter === "needs" ? { needs_response: true } : filter.startsWith("r") ? { rating: filter.slice(1) } : {};
    api.get("/reviews/seller", { params }).then((r) => setReviews(r.data.reviews)).catch(() => setReviews([]));
  };
  useEffect(() => { loadSummary(); loadRides(); }, []);
  useEffect(() => { loadReviews(); }, [filter]);

  const rep = summary?.reputation || {};

  return (
    <div data-testid="seller-reviews-page">
      <PageHeader title="Reviews & Ratings" icon={Star} subtitle="Customer feedback on your products. You can respond, but never change a customer's rating." />

      <div className="grid gap-4 md:grid-cols-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Product rating</p>
          {summary?.rating_count > 0 ? (
            <>
              <div className="mt-1 text-3xl font-extrabold text-slate-900" data-testid="seller-avg">{summary.rating_avg} ★</div>
              <Stars value={summary?.rating_avg} size={16} />
              <p className="mt-1 text-xs text-slate-500">{summary.rating_count} reviews</p>
              <div className="mt-3"><RatingDistribution dist={summary.rating_dist} total={summary.rating_count} /></div>
            </>
          ) : <p className="mt-2 text-sm text-slate-500">No reviews yet.</p>}
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <p className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-slate-400"><ShieldCheck className="h-3.5 w-3.5" /> Seller reputation</p>
          {rep.score != null ? (
            <>
              <div className="mt-1 text-3xl font-extrabold text-slate-900" data-testid="seller-reputation">{rep.score}<span className="text-base text-slate-400">/100</span></div>
              <p className="mt-1 text-xs text-slate-500">Separate from product rating — based on real fulfilment.</p>
            </>
          ) : <p className="mt-2 text-sm text-slate-500">Reputation appears once you have orders.</p>}
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5 text-sm text-slate-600">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Fulfilment metrics</p>
          <ul className="mt-2 space-y-1">
            <li>Orders: <b>{rep.total_orders ?? 0}</b></li>
            <li>Completion rate: <b>{rep.completion_rate ?? "—"}{rep.completion_rate != null ? "%" : ""}</b></li>
            <li>Cancellation rate: <b>{rep.cancellation_rate ?? "—"}{rep.cancellation_rate != null ? "%" : ""}</b></li>
            <li>Return rate: <b>{rep.return_rate ?? "—"}{rep.return_rate != null ? "%" : ""}</b></li>
            <li>Awaiting response: <b>{summary?.needs_response ?? 0}</b></li>
          </ul>
        </div>
      </div>

      <div className="mt-6 mb-3 flex flex-wrap gap-2">
        {[["all", "All"], ["needs", "Needs response"], ["r5", "5★"], ["r4", "4★"], ["r3", "3★"], ["r2", "2★"], ["r1", "1★"]].map(([v, label]) => (
          <button key={v} onClick={() => setFilter(v)} data-testid={`seller-filter-${v}`} className={`rounded-full px-3 py-1.5 text-sm ${filter === v ? "brand-gradient-bg text-white" : "border border-slate-200 text-slate-600"}`}>{label}</button>
        ))}
      </div>

      <div className="space-y-3" data-testid="seller-reviews-list">
        {reviews?.length === 0 && <p className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">No reviews match this filter.</p>}
        {(reviews || []).map((r) => <SellerReviewCard key={r.review_id} r={r} onChange={() => { loadReviews(); loadSummary(); }} />)}
      </div>

      {rideReviews.length > 0 && (
        <div className="mt-8" data-testid="seller-ride-reviews">
          <h2 className="mb-1 flex items-center gap-2 text-sm font-bold text-slate-800"><Car className="h-4 w-4 text-sky-600" /> Ride &amp; driver reviews</h2>
          <p className="mb-3 text-xs text-slate-500">Feedback riders left for your drivers. Your reply is shown publicly on your storefront.</p>
          <div className="space-y-3">
            {rideReviews.map((r) => <RideReviewCard key={r.booking_id} r={r} onChange={loadRides} />)}
          </div>
        </div>
      )}
    </div>
  );
}

function RideReviewCard({ r, onChange }) {
  const [replying, setReplying] = useState(false);
  const [text, setText] = useState(r.provider_reply || "");
  const [busy, setBusy] = useState(false);
  const respond = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try { await api.post(`/seller/driver-reviews/${r.booking_id}/reply`, { reply: text.trim() }); toast.success("Reply posted"); setReplying(false); onChange(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`ride-review-${r.booking_id}`}>
      <div className="flex items-center gap-2">
        <Stars value={r.driver_rating} size={14} />
        <span className="text-sm font-semibold text-slate-800">{r.driver_name}</span>
        <span className="text-xs text-slate-400">{r.service_name}{r.rated_at ? ` · ${new Date(r.rated_at).toLocaleDateString()}` : ""}</span>
      </div>
      {r.driver_review ? <p className="mt-1.5 text-sm text-slate-600">“{r.driver_review}”</p> : <p className="mt-1.5 text-sm italic text-slate-400">Rated {r.driver_rating}/5</p>}
      {r.provider_reply && !replying && (
        <div className="mt-2 rounded-lg border-l-2 border-sky-300 bg-slate-50 p-2.5">
          <p className="text-xs font-semibold text-sky-600">Your reply</p>
          <p className="text-sm text-slate-600">{r.provider_reply}</p>
        </div>
      )}
      {replying ? (
        <div className="mt-2">
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} data-testid={`ride-reply-input-${r.booking_id}`} className="w-full rounded-lg border border-slate-200 p-2 text-sm outline-none focus:border-sky-400" placeholder="Thank the rider or address their feedback…" />
          <div className="mt-1 flex gap-2">
            <button onClick={respond} disabled={busy} data-testid={`ride-reply-save-${r.booking_id}`} className="inline-flex items-center gap-1 rounded-lg brand-gradient-bg px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60">{busy && <Loader2 className="h-3 w-3 animate-spin" />} Post reply</button>
            <button onClick={() => setReplying(false)} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs text-slate-500">Cancel</button>
          </div>
        </div>
      ) : (
        <button onClick={() => setReplying(true)} data-testid={`ride-reply-btn-${r.booking_id}`} className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-sky-600"><MessageSquare className="h-3.5 w-3.5" /> {r.provider_reply ? "Edit reply" : "Reply"}</button>
      )}
    </div>
  );
}

function SellerReviewCard({ r, onChange }) {
  const [replying, setReplying] = useState(false);
  const [text, setText] = useState(r.seller_response?.body || "");
  const [busy, setBusy] = useState(false);

  const respond = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try { await api.post(`/reviews/${r.review_id}/seller-response`, { body: text.trim() }); toast.success("Response posted"); setReplying(false); onChange(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const flag = async () => {
    try { await api.post(`/reviews/${r.review_id}/seller-flag`, { reason: "Fake review" }); toast.success("Flagged for admin review"); onChange(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`seller-review-${r.review_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2"><Stars value={r.rating} size={14} /><span className="text-sm font-semibold text-slate-800">{r.display_name}</span></div>
          <p className="text-xs text-slate-400">{r.product_name} · {r.created_at ? new Date(r.created_at).toLocaleDateString() : ""}</p>
        </div>
        <button onClick={flag} data-testid={`seller-flag-${r.review_id}`} className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-rose-500"><Flag className="h-3.5 w-3.5" /> Flag</button>
      </div>
      {r.title && <p className="mt-1.5 text-sm font-medium text-slate-700">{r.title}</p>}
      {r.body && <p className="text-sm text-slate-600">{r.body}</p>}
      {r.images?.length > 0 && <div className="mt-2 flex gap-2">{r.images.map((s, i) => <img key={s.slice(-10) + i} src={s} alt="" className="h-14 w-14 rounded object-cover" />)}</div>}
      {r.seller_response && !replying && (
        <div className="mt-2 rounded-lg border-l-2 border-blue-300 bg-slate-50 p-2.5">
          <p className="text-xs font-semibold text-blue-600">Your response</p>
          <p className="text-sm text-slate-600">{r.seller_response.body}</p>
        </div>
      )}
      {replying ? (
        <div className="mt-2">
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} data-testid={`seller-reply-input-${r.review_id}`} className="w-full rounded-lg border border-slate-200 p-2 text-sm outline-none focus:border-blue-400" placeholder="Write a professional response…" />
          <div className="mt-1 flex gap-2">
            <button onClick={respond} disabled={busy} data-testid={`seller-reply-save-${r.review_id}`} className="inline-flex items-center gap-1 rounded-lg brand-gradient-bg px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60">{busy && <Loader2 className="h-3 w-3 animate-spin" />} Post</button>
            <button onClick={() => setReplying(false)} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs text-slate-500">Cancel</button>
          </div>
        </div>
      ) : (
        <button onClick={() => setReplying(true)} data-testid={`seller-reply-btn-${r.review_id}`} className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-blue-600"><MessageSquare className="h-3.5 w-3.5" /> {r.seller_response ? "Edit response" : "Respond"}</button>
      )}
    </div>
  );
}
